import React, { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend
} from 'chart.js';
import { format, parseISO } from 'date-fns';

// Импортируем функции из API
import {
  getColumnByKey,
  getColumnLabel,
  getColumnColor,
  getChartColumns,
  getRegularChartColumns,
  getChartGroupColumns,
  getRelatedPercentageColumns,
  getPercentageSourceText,
  calculateColumnValue,
  calculateGroupMetricValues,
  shouldShowOnChart,
  isPercentageColumn,
  getGroupMetrics,
  getGroupColors,
  getGroupName
} from './columnsAPI';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Функция для определения доступных периодов на основе наличия почасовых данных
const getAvailableTimePeriods = (hasHourlyData, defaultPeriods) => {
  if (hasHourlyData) {
    // Если есть почасовые данные - показываем все периоды
    return defaultPeriods;
  } else {
    // Если нет почасовых данных - только day и week
    return defaultPeriods.filter(period => 
      ['day', 'week'].includes(period.value)
    );
  }
};

// Опции временного периода по умолчанию
const DEFAULT_TIME_PERIODS = [
  { value: 'hour', label: '1 hour' },
  { value: 'hour4', label: '4 hours' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' }
];

// Типы графиков
const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' }
];

// Fallback цвета для случаев, когда в метаданных не указан цвет
const FALLBACK_COLORS = [
  '#10B981', '#EF4444', '#F59E0B', '#06B6D4', '#84CC16', '#EC4899', 
  '#6366F1', '#F97316', '#14B8A6', '#8B5CF6'
];

const ChartComponent = ({ 
  statistics, 
  selectedPeriod: externalSelectedPeriod,
  onPeriodChange,
  timePeriods = DEFAULT_TIME_PERIODS,
  columnsMetadata = []
}) => {
  const [internalSelectedPeriod, setInternalSelectedPeriod] = useState('day');
  const selectedPeriod = externalSelectedPeriod || internalSelectedPeriod;

  // Определяем есть ли почасовые данные
  const hasHourlyData = statistics?.hourly_stats?.length > 0;
  
  // Получаем доступные периоды
  const availableTimePeriods = getAvailableTimePeriods(hasHourlyData, timePeriods);
  
  const [chartType, setChartType] = useState('bar');
  const [chartData, setChartData] = useState(null);
  const [rawDataByLabel, setRawDataByLabel] = useState({});
  const [fullColumnsMetadata, setFullColumnsMetadata] = useState([]);

  // Автоматически переключаем период, если текущий недоступен
  useEffect(() => {
    const isCurrentPeriodAvailable = availableTimePeriods.some(
      period => period.value === selectedPeriod
    );
    
    if (!isCurrentPeriodAvailable) {
      const newPeriod = 'day';
      setInternalSelectedPeriod(newPeriod);
      
      if (onPeriodChange) {
        onPeriodChange(newPeriod);
      }
    }
  }, [hasHourlyData, selectedPeriod, availableTimePeriods, onPeriodChange]);

  useEffect(() => {
    if (statistics && (statistics.hourly_stats || statistics.daily_stats) && columnsMetadata.length > 0) {
      prepareChartData(selectedPeriod);
    }
  }, [statistics, selectedPeriod, chartType, columnsMetadata]);


  // Функция для проверки и нормализации данных
  const normalizeData = (sourceData) => {
    return sourceData.map(stat => {
      const registrationSuccess = stat.registration_success || 0;
      const smsSuccess = stat.sms_success || 0;
      const correctedRegistrationSuccess = Math.min(registrationSuccess, smsSuccess);
      
      return {
        ...stat,
        registration_success: correctedRegistrationSuccess
      };
    });
  };

  const prepareChartData = (period) => {
    const sourceData = statistics.hourly_stats && statistics.hourly_stats.length > 0 
      ? statistics.hourly_stats 
      : statistics.daily_stats;
    
    if (!sourceData || sourceData.length === 0) {
      setChartData(null);
      return;
    }
    
    const normalizedData = normalizeData(sourceData);
    
    // Получаем колонки для отображения на графике
    const regularColumns = getRegularChartColumns(columnsMetadata);
    const chartGroupColumns = getChartGroupColumns(columnsMetadata);
    const allChartColumns = getChartColumns(columnsMetadata); // Все колонки для line chart
    
    if (allChartColumns.length === 0) {
      console.warn('No chart columns found in metadata');
      setChartData(null);
      return;
    }
    
    // Обрабатываем данные по времени
    const timeData = normalizedData.map(stat => {
      let dateObj;
      if (typeof stat.date === 'string') {
        dateObj = parseISO(stat.date);
      } else {
        dateObj = new Date(stat.date);
      }
      
      const dateStr = dateObj.toISOString();
      
      // ИСПРАВЛЕНИЕ: Начинаем с исходных данных stat (не создаем пустой объект)
      const processedData = { ...stat, date: dateStr };
      
      // Вычисляем колонки в правильном порядке для корректных зависимостей
      
      // 1. Сначала base колонки
      columnsMetadata
        .filter(col => col.column_type === 'base')
        .forEach(column => {
          processedData[column.key] = calculateColumnValue(column.key, stat, columnsMetadata);
        });

      // 2. Потом error_category
      columnsMetadata
        .filter(col => col.column_type === 'error_category')
        .forEach(column => {
          processedData[column.key] = calculateColumnValue(column.key, stat, columnsMetadata);
        });

      // 3. Потом calculated (они могут зависеть от base и error_category)
      columnsMetadata
        .filter(col => col.column_type === 'calculated')
        .forEach(column => {
          processedData[column.key] = calculateColumnValue(column.key, stat, columnsMetadata);
        });

      // 4. Затем percentage колонки (используем processedData для правильных зависимостей)
      columnsMetadata
        .filter(col => ['percentage_ratio', 'percentage_attempts'].includes(col.column_type))
        .forEach(column => {
          processedData[column.key] = calculateColumnValue(column.key, processedData, columnsMetadata);
        });

      // 5. Chart groups в конце
      columnsMetadata
        .filter(col => col.column_type === 'chart_group')
        .forEach(column => {
          processedData[column.key] = calculateColumnValue(column.key, processedData, columnsMetadata);
        });
      
      return processedData;
    });

    // Сортируем по дате
    timeData.sort((a, b) => a.date.localeCompare(b.date));

    // Группируем данные в зависимости от выбранного периода
    const groupedData = {};
    const rawData = {};
    
    const getPeriodStart = (dateString) => {
      const date = new Date(dateString);
      let year = date.getUTCFullYear();
      let month = date.getUTCMonth();
      let day = date.getUTCDate();
      let hours = date.getUTCHours();
      
      switch (period) {
        case 'hour':
          return new Date(Date.UTC(year, month, day, hours, 0, 0, 0)).toISOString();
        case 'hour4':
          const fourHourBlock = Math.floor(hours / 4) * 4;
          return new Date(Date.UTC(year, month, day, fourHourBlock, 0, 0, 0)).toISOString();
        case 'day':
          return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
        case 'week':
          const dateObj = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
          const dayOfWeek = dateObj.getUTCDay();
          const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          dateObj.setUTCDate(dateObj.getUTCDate() - diff);
          return dateObj.toISOString();
        default:
          return new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
      }
    };
    
    const getNextPeriod = (dateString) => {
      const date = new Date(dateString);
      
      switch (period) {
        case 'hour':
          date.setUTCHours(date.getUTCHours() + 1);
          return date.toISOString();
        case 'hour4':
          date.setUTCHours(date.getUTCHours() + 4);
          return date.toISOString();
        case 'day':
          date.setUTCDate(date.getUTCDate() + 1);
          return date.toISOString();
        case 'week':
          date.setUTCDate(date.getUTCDate() + 7);
          return date.toISOString();
        default:
          date.setUTCDate(date.getUTCDate() + 1);
          return date.toISOString();
      }
    };
    
    const formatPeriodLabel = (dateString) => {
      const date = new Date(dateString);
      
      switch (period) {
        case 'hour':
          return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth()+1).toString().padStart(2, '0')} ${date.getUTCHours().toString().padStart(2, '0')}:00`;
        case 'hour4':
          const endHour = (date.getUTCHours() + 3) % 24;
          return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth()+1).toString().padStart(2, '0')} ${date.getUTCHours().toString().padStart(2, '0')}:00-${endHour.toString().padStart(2, '0')}:00`;
        case 'day':
          return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth()+1).toString().padStart(2, '0')}/${date.getUTCFullYear()}`;
        case 'week':
          const endDate = new Date(date);
          endDate.setUTCDate(endDate.getUTCDate() + 6);
          return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth()+1).toString().padStart(2, '0')}-${endDate.getUTCDate().toString().padStart(2, '0')}/${(endDate.getUTCMonth()+1).toString().padStart(2, '0')}`;
        default:
          return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth()+1).toString().padStart(2, '0')}/${date.getUTCFullYear()}`;
      }
    };

    // Инициализируем группы данных
    if (timeData.length > 0) {
      let currentDate = getPeriodStart(timeData[0].date);
      const endDate = getPeriodStart(timeData[timeData.length - 1].date);
      
      while (currentDate <= endDate) {
        const periodLabel = formatPeriodLabel(currentDate);
        
        // Инициализируем данные для всех колонок
        groupedData[periodLabel] = {};
        rawData[periodLabel] = { date: currentDate };
        
        columnsMetadata.forEach(column => {
          groupedData[periodLabel][column.key] = 0;
          rawData[periodLabel][column.key] = 0;
        });
        
        currentDate = getNextPeriod(currentDate);
      }
      
      // ИСПРАВЛЕННАЯ логика группировки данных
      timeData.forEach(item => {
        try {
          const periodStart = getPeriodStart(item.date);
          const periodLabel = formatPeriodLabel(periodStart);
          
          if (groupedData[periodLabel]) {
            // ИСПРАВЛЕНИЕ: Сохраняем ВСЕ поля (включая исходные base поля)
            Object.keys(item).forEach(key => {
              if (key === 'date') return; // Пропускаем дату
              
              const column = getColumnByKey(key, columnsMetadata);
              
              if (column?.is_percentage) {
                // Процентные колонки НЕ суммируем
                return;
              }
              
              // Суммируем все остальные поля (включая base поля из исходных данных)
              const value = item[key] || 0;
              if (!groupedData[periodLabel][key]) {
                groupedData[periodLabel][key] = 0;
                rawData[periodLabel][key] = 0;
              }
              groupedData[periodLabel][key] += value;
              rawData[periodLabel][key] += value;
            });
          }
        } catch (error) {
          console.error('Error processing data item:', item, error);
        }
      });

      // НОВЫЙ ШАГ: Пересчитываем процентные колонки для каждого периода
      Object.keys(groupedData).forEach(periodLabel => {
        const periodData = groupedData[periodLabel];
        
        // Пересчитываем процентные колонки на основе суммированных данных
        columnsMetadata.forEach(column => {
          if (column.is_percentage) {
            const recalculatedValue = calculateColumnValue(column.key, periodData, columnsMetadata);
            groupedData[periodLabel][column.key] = recalculatedValue;
            rawData[periodLabel][column.key] = recalculatedValue;
          }
        });
      });
    }

    setRawDataByLabel(rawData);

    // Создаем datasets для графика
    const labels = Object.keys(groupedData);
    const datasets = [];

    if (chartType === 'line') {
      // Line Chart: показываем ВСЕ колонки как отдельные линии
      allChartColumns.forEach((column, index) => {
        const color = getColumnColor(column.key, columnsMetadata) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
        
        datasets.push({
          label: getColumnLabel(column.key, columnsMetadata),
          data: labels.map(label => groupedData[label][column.key] || 0),
          borderColor: color,
          backgroundColor: color + '20',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
          tension: 0.1
        });
      });
    } else {
      // Bar Chart: только группированные колонки
      chartGroupColumns.forEach(groupColumn => {
        const groupMetrics = getGroupMetrics(groupColumn.key, columnsMetadata);
        const groupColors = getGroupColors(groupColumn.key, columnsMetadata);
        const groupName = getGroupName(groupColumn.key, columnsMetadata);
        
        groupMetrics.forEach((metricKey, index) => {
          const metricLabel = getColumnLabel(metricKey, columnsMetadata);
          const color = groupColors[metricKey] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
          
          
          datasets.push({
            label: `${groupName}: ${metricLabel}`,
            data: labels.map(label => {
              const groupValues = calculateGroupMetricValues(groupColumn.key, rawData[label], columnsMetadata);
              return groupValues[metricKey] || 0;
            }),
            backgroundColor: color,
            borderColor: color,
            borderWidth: 1,
            stack: groupColumn.key,
          });
        });
      });
    }
    
    setChartData({ labels, datasets });
  };

  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    setInternalSelectedPeriod(newPeriod);
    
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };
  
  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const formatDate = (dateString, periodType) => {
    try {
      let date;
      if (typeof dateString === 'string') {
        date = parseISO(dateString);
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        console.error('Invalid date value:', dateString);
        return 'Invalid date';
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date after parsing:', dateString);
        return 'Invalid date';
      }
      
      switch (periodType) {
        case 'hour':
          return format(date, "dd MMM yyyy HH:00 'UTC'");
        case 'hour4':
          const nextHour = new Date(date);
          nextHour.setUTCHours(date.getUTCHours() + 3);
          return `${format(date, "dd MMM HH:00")} - ${format(nextHour, "HH:00 'UTC'")}`;
        case 'day':
          return format(date, "dd MMMM yyyy 'UTC'");
        case 'week':
          const endOfWeek = new Date(date);
          endOfWeek.setUTCDate(date.getUTCDate() + 6);
          return `${format(date, "dd MMM yyyy")} - ${format(endOfWeek, "dd MMM yyyy 'UTC'")}`;
        default:
          return format(date, "dd MMM yyyy 'UTC'");
      }
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid date';
    }
  };

  // Настройки тултипов с поддержкой групп
  const tooltipSettings = {
    enabled: true,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    titleFont: {
      weight: 'bold',
      size: 14
    },
    bodyFont: {
      size: 12
    },
    padding: 12,
    displayColors: true,
    boxWidth: 12,
    boxHeight: 12,
    usePointStyle: true,
    callbacks: {
      title: function(tooltipItems) {
        try {
          const dataLabel = tooltipItems[0].label;
          const rawData = rawDataByLabel[dataLabel];
          
          if (rawData && rawData.date) {
            return formatDate(rawData.date, selectedPeriod);
          }
          
          return dataLabel;
        } catch (error) {
          console.error('Error in tooltip title:', error);
          return 'Error';
        }
      },
      label: function(context) {
        const datasetLabel = context.dataset.label;
        const value = context.raw;

        // Функция для извлечения ключа метрики из label
        const extractMetricKeyFromLabel = (label) => {
          const parts = label.split(': ');
          if (parts.length >= 2) {
            const metricLabel = parts[1];
            const column = columnsMetadata.find(col => col.label === metricLabel);
            return column?.key || null;
          }
          return null;
        };
        
        if (chartType === 'bar') {
          // Bar Chart: персональные тултипы С ПРОЦЕНТАМИ
          const dataLabel = context.label;
          const rawData = rawDataByLabel[dataLabel];
          
          if (!rawData) {
            return `${datasetLabel}: ${value.toLocaleString()}`;
          }

          // Находим базовую метрику из label (например "Reg Group: Reg Unfinished" → "reg_unfinished")
          const metricKey = extractMetricKeyFromLabel(datasetLabel);
          const baseColumn = getColumnByKey(metricKey, columnsMetadata);
          
          if (!baseColumn) {
            return `${datasetLabel}: ${value.toLocaleString()}`;
          }

          // Создаем расширенный тултип с процентами
          const tooltipLines = [`${datasetLabel}: ${value.toLocaleString()}`];
          
          const relatedPercentages = getRelatedPercentageColumns(baseColumn.key, columnsMetadata);
          relatedPercentages.forEach(percentColumn => {
            const percentValue = calculateColumnValue(percentColumn.key, rawData, columnsMetadata);
            const sourceText = getPercentageSourceText(percentColumn, columnsMetadata);
            tooltipLines.push(`${percentColumn.label}: ${percentValue}% (${sourceText})`);
          });
          
          return tooltipLines;
        } else {
          // Line Chart: расширенные тултипы с процентными данными
          const dataLabel = context.label;
          const rawData = rawDataByLabel[dataLabel];
          
          if (!rawData) {
            return `${datasetLabel}: ${value.toLocaleString()}`;
          }

          const baseColumn = columnsMetadata.find(col => {
            const columnLabel = getColumnLabel(col.key, columnsMetadata);
            return columnLabel === datasetLabel;
          });
          
          if (!baseColumn) {
            return `${datasetLabel}: ${value.toLocaleString()}`;
          }
          
          // Создаем массив для всех значений (базовое + связанные процентные)
          const tooltipLines = [];
          
          // Добавляем базовое значение
          tooltipLines.push(`${baseColumn.label}: ${value.toLocaleString()}`);
          
          // Ищем связанные процентные колонки
          const relatedPercentages = getRelatedPercentageColumns(baseColumn.key, columnsMetadata);
          
          relatedPercentages.forEach(percentColumn => {
            const percentValue = calculateColumnValue(percentColumn.key, rawData, columnsMetadata);
            
            const sourceText = getPercentageSourceText(percentColumn, columnsMetadata);
            tooltipLines.push(`${percentColumn.label}: ${percentValue}% (${sourceText})`);
          });
          
          return tooltipLines;
        }
      }
    }
  };

  // Настройки графика
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: chartType === 'bar' ? 'nearest' : 'index',
      intersect: chartType === 'bar',
    },
    plugins: {
      tooltip: tooltipSettings,
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          boxWidth: 12,
          generateLabels: function(chart) {
            const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original.call(this, chart);
            
            if (chartType === 'bar') {
              const groupedLabels = [];
              const usedLabels = new Set();
              
              const chartGroupColumns = getChartGroupColumns(columnsMetadata);
              chartGroupColumns.forEach(groupColumn => {
                const groupName = getGroupName(groupColumn.key, columnsMetadata);
                const groupMetrics = getGroupMetrics(groupColumn.key, columnsMetadata);
                
                groupMetrics.forEach(metricKey => {
                  const metricLabel = getColumnLabel(metricKey, columnsMetadata);
                  const fullLabel = `${groupName}: ${metricLabel}`;
                  
                  const existingLabel = labels.find(l => l.text === fullLabel);
                  if (existingLabel && !usedLabels.has(fullLabel)) {
                    groupedLabels.push(existingLabel);
                    usedLabels.add(fullLabel);
                  }
                });
              });
              
              return groupedLabels;
            }
            
            return labels;
          }
        }
      },
      title: { display: false }
    },
    scales: {
      x: {
        stacked: chartType === 'bar',
        grid: { display: false },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: true,
          font: { size: 10 },
          color: '#666'
        },
        border: { width: 1 }
      },
      y: {
        stacked: chartType === 'bar',
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10
      }
    },
    animation: { duration: 300 },
    hover: {
      mode: chartType === 'bar' ? 'nearest' : 'index',
      intersect: chartType === 'bar'
    }
  };

  // Вычисляем суммы для информационных блоков
  const calculateTotals = () => {
    if (!statistics || !statistics.daily_stats || statistics.daily_stats.length === 0 || columnsMetadata.length === 0) {
      return { totals: {}, displayColumns: [], groupTotals: {} };
    }
    
    const totals = {};
    const groupTotals = {};
    let displayColumns = [];
    
    if (chartType === 'line') {
      // Для line chart показываем все колонки
      displayColumns = getChartColumns(columnsMetadata);
      
      displayColumns.forEach(column => {
        totals[column.key] = statistics.daily_stats.reduce((sum, stat) => {
          return sum + calculateColumnValue(column.key, stat, columnsMetadata);
        }, 0);
      });
    } else {
      // Для bar chart показываем групповые блоки
      const chartGroupColumns = getChartGroupColumns(columnsMetadata);
      
      chartGroupColumns.forEach(groupColumn => {
        const groupKey = groupColumn.key;
        const groupMetrics = getGroupMetrics(groupKey, columnsMetadata);
        
        groupTotals[groupKey] = {};
        groupMetrics.forEach(metricKey => {
          groupTotals[groupKey][metricKey] = statistics.daily_stats.reduce((sum, stat) => {
            const metricValues = calculateGroupMetricValues(groupKey, stat, columnsMetadata);
            return sum + (metricValues[metricKey] || 0);
          }, 0);
        });
        
        // Общая сумма группы
        totals[groupKey] = Object.values(groupTotals[groupKey]).reduce((sum, value) => sum + value, 0);
      });
      
      displayColumns = chartGroupColumns;
    }
    
    return { totals, displayColumns, groupTotals };
  };

  const { totals, displayColumns, groupTotals } = calculateTotals();

  return (
    <div className="w-full bg-white shadow-md rounded-lg p-4 flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-700">Statistics Chart</h3>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <label htmlFor="chart-type-select" className="mr-2 text-sm text-gray-600">Chart Type:</label>
            <select 
              id="chart-type-select"
              value={chartType} 
              onChange={handleChartTypeChange}
              className="border border-gray-300 rounded-md shadow-sm px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {CHART_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="period-select" className="mr-2 text-sm text-gray-600">Interval:</label>
            <select 
              id="period-select"
              value={selectedPeriod} 
              onChange={handlePeriodChange}
              className="border border-gray-300 rounded-md shadow-sm px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableTimePeriods.map(period => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!hasHourlyData && (
          <div className="text-xs text-gray-500 italic">
            Hourly data available only for periods ≤ 3 days
          </div>
        )}
      </div>
      
      <div className="h-96 w-full">
        {chartData ? (
          chartType === 'bar' ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )
        ) : (
          <div className="h-full w-full flex justify-center items-center">
            <p className="text-gray-500">Loading chart data...</p>
          </div>
        )}
      </div>
      
      {/* Информационные блоки */}
      {statistics && statistics.daily_stats && displayColumns.length > 0 && (
        <div className="mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full">
            {displayColumns.map((column, index) => {
              if (chartType === 'bar' && column.column_type === 'chart_group') {
                // Для bar chart показываем групповые блоки
                const groupColors = getGroupColors(column.key, columnsMetadata);
                const groupMetrics = getGroupMetrics(column.key, columnsMetadata);
                const groupName = getGroupName(column.key, columnsMetadata);
                const groupTotal = totals[column.key] || 0;
                
                return (
                  <div key={column.key} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="text-sm font-medium text-gray-700 mb-3">{groupName}</div>
                    <div className="space-y-2">
                      {groupMetrics.map(metricKey => {
                        const metricLabel = getColumnLabel(metricKey, columnsMetadata);
                        const metricColor = groupColors[metricKey] || '#666';
                        const metricTotal = groupTotals[column.key]?.[metricKey] || 0;
                        
                        return (
                          <div key={metricKey} className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: metricColor }}
                              ></div>
                              <span className="text-sm text-gray-600">{metricLabel}</span>
                            </div>
                            <span className="text-lg font-semibold text-gray-800">
                              {metricTotal.toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                      <div className="border-t pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">Total:</span>
                          <span className="text-xl font-bold text-gray-900">
                            {groupTotal.toLocaleString()}
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // Для line chart показываем обычные блоки
                const color = getColumnColor(column.key, columnsMetadata) || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
                const total = totals[column.key] || 0;
                
                return (
                  <div key={column.key} className="bg-gray-50 p-3 rounded-md">
                    <div className="text-xs text-gray-500 mb-1">{column.label}</div>
                    <div 
                      className="text-xl font-semibold text-gray-800" 
                      style={{ color }}
                    >
                      {total.toLocaleString()}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}

      {/* Информация о том, что в bar chart отображаются только группы */}
      {chartType === 'bar' && getChartGroupColumns(columnsMetadata).length === 0 && (
        <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
          <div className="text-sm text-yellow-800 font-medium mb-1">
            ⚠️ No Chart Groups Found
          </div>
          <div className="text-xs text-yellow-700">
            Create chart groups in the constructor to see grouped bar charts
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartComponent;